import { cn } from "@newxsystems/shared";
import type { IFullWidthVideoFeatureBlockProps } from "./FullWidthVideoFeatureBlock.props";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { FullWidthVideoFeatureVideo } from "./FullWidthVideoFeatureVideo.client";

export const FullWidthVideoFeatureBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,
  parentClass,
  poster,
  spacing,
  subheading,
  video,
}: IFullWidthVideoFeatureBlockProps) => {
  const Link = resolveKaajPrimitive("Link");
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;
  const hasIntro = Boolean(eyebrow || heading || subheading || buttons?.length);

  if (
    !video ||
    (!hasIntro && !video.embedUrl && !poster?.defaultSrc && !video.poster?.defaultSrc)
  ) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden px-6 md:px-10",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      id={anchorId ?? undefined}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        {eyebrow || heading || subheading || buttons?.length ? (
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
            {eyebrow ? (
              <p className="text-sm font-semibold uppercase tracking-[0.18em]">{eyebrow}</p>
            ) : null}
            {heading ? (
              <h2 className="kaaj-section-heading" id={headingId}>
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <div
                className="text-base leading-8 md:text-lg"
                dangerouslySetInnerHTML={{ __html: subheading }}
              />
            ) : null}
            {buttons?.length ? (
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                {buttons.map((button) => (
                  <Link
                    aria-label={button.ariaLabel}
                    className="rounded-full px-5 py-3 text-sm font-semibold"
                    href={button.href}
                    key={button.href}
                    rel={button.target === "_blank" ? "noopener noreferrer" : undefined}
                    target={button.target}
                  >
                    {button.text}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <figure className="overflow-hidden rounded-3xl shadow-2xl">
          <FullWidthVideoFeatureVideo poster={poster ?? video.poster} video={video} />
          {video.caption ? (
            <figcaption className="px-5 py-4 text-sm">{video.caption}</figcaption>
          ) : null}
        </figure>
      </div>
    </section>
  );
};

FullWidthVideoFeatureBlock.displayName = "Block::FullWidthVideoFeatureBlock";
